/**
 * Shared presentation pieces for the MCX and NFO Opportunities tables.
 *
 * Display only: every value shown here is a field the backend already returned for the row.
 * Nothing is recalculated — the Info view simply holds the columns that no longer fit in
 * the default table (Spread, DTE, Lot, Funds (L), Margin (M), Status, DIR, Why).
 */
import { WorkbookNote } from "@/components/FuturesScenario";
import React, { useState } from "react";
import { Info, SlidersHorizontal, X } from "lucide-react";
import TerminalDrawer from "./TerminalDrawer";
import ResetDefaultsButton from "./ResetDefaultsButton";
import { DEFAULT_OI_MIN } from "@/lib/opportunityFilters";
import { inr, num, compact } from "@/lib/format";

export const MD_STATUS_CLS = {
  OK: "bg-[#00C805]/15 text-[#00C805]", STALE: "bg-[#FFCC00]/15 text-[#FFCC00]",
  WAITING_FOR_TICKS: "bg-[#475569]/25 text-slate-400",
  NO_FUTURES_CONTRACTS: "bg-[#FF6b62]/15 text-[#FF6b62]",
  NO_SPOT_INSTRUMENT: "bg-[#FF6b62]/15 text-[#FF6b62]",
};

export function DirPill({ direction }) {
  if (!direction) return <span className="text-slate-600">—</span>;
  const far = direction === "SELL_FAR_BUY_NEAR";
  return (
    <span className={`text-[9px] px-1 rounded-sm font-bold ${far ? "bg-[#2563EB]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
      {far ? "SELL FAR" : "SELL NEAR"}
    </span>
  );
}

/** The exact tradable symbol, allowed to wrap after the exchange prefix instead of forcing
 *  the table wider. */
export function ExactSymbol({ symbol, cls, testId }) {
  if (!symbol) return <span className="text-slate-600">—</span>;
  const [ex, ts] = String(symbol).includes(":") ? String(symbol).split(":") : [null, symbol];
  return (
    <span className={`text-[10px] leading-tight ${cls || ""}`} data-testid={testId} title={symbol}>
      {ex ? <>{ex}:<wbr />{ts}</> : ts}
    </span>
  );
}

/** Open interest, compact (20.0K / 1.25L); the exact figure is in the tooltip. */
/** Which contract pair a row is: Near+Mid or Mid+Far (Phase 0 DTE rule). */
export function PairCell({ row, testId }) {
  const sel = row.pair_selection || "NEAR_MID";
  const label = sel === "MID_FAR" ? "MID+FAR" : "NEAR+MID";
  return (
    <span data-testid={testId} title={`Near DTE ${row.near_dte ?? "—"} · pair DTE ${row.pair_dte ?? "—"}`}
      className={`text-[9px] font-bold px-1 py-0.5 rounded-sm whitespace-nowrap ${sel === "MID_FAR" ? "bg-[#7c3aed]/20 text-[#c4b5fd]" : "bg-[#2563EB]/15 text-[#5b9bff]"}`}>
      {label}{row.pair_dte != null ? ` · ${row.pair_dte}d` : ""}
    </span>
  );
}

/** The caller's active trade on this underlying, if any: one active trade per underlying per
 *  environment — a new one is refused; Roll Over moves the existing trade instead. */
export function ActiveTradeHint({ active, testId }) {
  if (!active || active.length === 0) return null;
  return (
    <div className="text-[9px] text-[#FFCC00] mt-0.5 whitespace-nowrap" data-testid={testId}
      title="An active trade already exists for this underlying. Complete, stop, or roll over the existing trade before creating another trade for this underlying.">
      active: {active.map((a) => `${a.name} (${a.environment})`).join(", ")}
    </div>
  );
}

export function OiCell({ value }) {
  if (value === null || value === undefined) return <span className="text-slate-600">—</span>;
  return <span title={Number(value).toLocaleString("en-IN")}>{compact(Number(value))}</span>;
}

function InfoRow({ label, children }) {
  return (
    <>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 self-center">{label}</div>
      <div className="text-[12px] mono text-slate-100 text-right">{children}</div>
    </>
  );
}

/**
 * Per-row Info view (the app's standard side drawer: keyboard focus, Escape, focus restore).
 * `market` is MCX or NFO; `onWhy` (MCX) opens the existing Strategy Inspector for that commodity.
 */
export function OpportunityInfo({ row, market, onWhy, testId }) {
  const [open, setOpen] = useState(false);
  const nfo = market === "NFO";
  const name = nfo ? row.name : row.commodity;
  const spread = nfo ? row.winning_spread
    : Math.max(row.spread_sell_far_buy_near, row.spread_buy_far_sell_near);
  return (
    <>
      <button type="button" data-testid={testId} aria-label={`Details for ${name}`} onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] text-slate-300 hover:bg-[#232730]">
        <Info className="h-3 w-3" /> Info
      </button>
      {open && (
      <TerminalDrawer title={`${name} · Opportunity details`} onClose={() => setOpen(false)} width={380}
        testId={`${testId}-content`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] mono text-slate-400">#{row.rank} · %Profit {(Number(row.pct_profit) * 100).toFixed(3)}%</span>
          <DirPill direction={row.direction} />
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <InfoRow label="Spread">{num(spread, 3)}</InfoRow>
          {nfo && <InfoRow label="H (sell far − buy near)">{num(row.spread_sell_far_buy_near, 3)}</InfoRow>}
          {nfo && <InfoRow label="I (buy far − sell near)">{num(row.spread_buy_far_sell_near, 3)}</InfoRow>}
          <InfoRow label="DTE">{row.days_to_expiry ?? "—"}</InfoRow>
          {/* The current broker/exchange lot only. The old workbook lot is not shown. */}
          <InfoRow label="Lot">{row.lot ?? "—"}</InfoRow>
          <InfoRow label="Funds (L)">{inr(row.funds_required)}</InfoRow>
          <InfoRow label="Margin (M)">{inr(row.margin)}</InfoRow>
          <div className="col-span-2"><WorkbookNote row={row} /></div>
          <InfoRow label="Status">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${MD_STATUS_CLS[row.market_data_status] || "bg-[#475569]/25 text-slate-400"}`}>{row.market_data_status}</span>
          </InfoRow>
          <InfoRow label="DIR"><DirPill direction={row.direction} /></InfoRow>
          {nfo && <InfoRow label="Spot symbol"><span className="text-slate-400 text-[11px]">{row.spot_symbol || "—"}</span></InfoRow>}
        </div>
        <div className="mt-3 pt-2 border-t border-[#2A2F3A]">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Why</div>
          {onWhy ? (
            <button type="button" onClick={() => { setOpen(false); onWhy(); }} data-testid={`${testId}-why`}
              className="text-[11px] font-bold px-2 py-1 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">
              Open Strategy Inspector (decision audit)
            </button>
          ) : (
            <div className="text-[11px] text-slate-400 leading-snug">
              Ranked by %Profit (N). The leg choice is the larger of H and I above; the status above
              decides whether the row can be added.
            </div>
          )}
        </div>
      </TerminalDrawer>
      )}
    </>
  );
}

/** Profit % Min and OI Min, shared by MCX and NFO. Blank or 0 = filter off. */
export function OpportunityFilterBar({ filters, onChange, shown, total }) {
  const set = (k) => (e) => onChange({ ...filters, [k]: e.target.value });
  const active = (filters.profitPctMin !== "" && Number(filters.profitPctMin) > 0)
    || (filters.oiMin !== "" && Number(filters.oiMin) > 0);
  const inputCls = "w-20 bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-1.5 py-1 text-xs mono outline-none focus:border-[#2563EB]";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="opp-filter-bar">
      <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
      <label className="flex items-center gap-1.5">
        <span className="text-slate-500">Profit % Min</span>
        <input type="number" min="0" step="0.1" value={filters.profitPctMin} onChange={set("profitPctMin")}
          placeholder="off" className={inputCls} data-testid="filter-profit-min" aria-label="Minimum %Profit" />
      </label>
      <label className="flex items-center gap-1.5" title="Applies to BOTH legs: Near OI and Far OI must each be at least this">
        <span className="text-slate-500">OI Min (both legs)</span>
        <input type="number" min="0" step="1000" value={filters.oiMin} onChange={set("oiMin")}
          placeholder="off" className={inputCls} data-testid="filter-oi-min" aria-label="Minimum open interest on both legs" />
      </label>
      {active && (
        <button type="button" onClick={() => onChange({ profitPctMin: "", oiMin: "" })} data-testid="filter-clear"
          className="inline-flex items-center gap-1 px-1.5 py-1 rounded-sm text-slate-400 hover:text-slate-200 border border-[#2A2F3A]">
          <X className="h-3 w-3" /> Clear
        </button>
      )}
      {/* the recommended filters: OI Min on both legs (illiquid legs may never fill), no profit floor */}
      {(filters.profitPctMin !== "" || filters.oiMin !== DEFAULT_OI_MIN) && (
        <ResetDefaultsButton onReset={() => onChange({ profitPctMin: "", oiMin: DEFAULT_OI_MIN })} testId="opp-reset-defaults"
          className="h-7" title={`Profit % Min off, OI Min (both legs) ${DEFAULT_OI_MIN}`} />
      )}
      {total != null && (
        <span className="text-slate-500 mono" data-testid="filter-count">{shown} of {total} shown</span>
      )}
    </div>
  );
}
